/**
 * WordLevelAuthorship — V2.3 inline span rendering.
 *
 * Renders the server-side `authorship_runs` JSON sidecar attr (set by
 * content-api V2.3 P2, deployed 2026-06-22) as subtle inline background
 * tints per author. Complements the paragraph-level border-bar from
 * `AuthorHighlight.ts`:
 *   - AuthorHighlight  → left border, "wer hat den Absatz angefangen"
 *   - WordLevelAuthorship → inline tint, "welche Wörter sind von wem"
 *
 * The two compose: a paragraph with one author shows ONE border + one
 * uniform tint (effectively invisible because the run covers all chars
 * and we suppress single-run tinting). A paragraph with intra-paragraph
 * refinement (e.g. Agent B replaced a word inside Agent A's text via
 * `refine_phrase` → V2.3.1 run-splitting) shows the border in A's color
 * plus a colored band over B's word.
 *
 * --- Data shape (Backend-Contract V2.3.1) ---
 *
 *   authorship_runs: JSON-string,
 *   parsed = [
 *     { start: 0, end: 15, author_id: 'agent:A', author_color: 'hsl(...)', ... },
 *     { start: 15, end: 19, author_id: 'agent:B', author_color: 'hsl(...)', ... },
 *     { start: 19, end: 38, author_id: 'agent:A', author_color: 'hsl(...)', ... },
 *   ]
 *
 * Offsets are Unicode code-point offsets into the paragraph's
 * materialised plain text. Lückenlose Coverage 0..len(plain_text).
 * Adjacent runs with identical author_id+commit_id are merged
 * server-side (see content-api content/services/authorship_runs.py).
 *
 * --- Position mapping ---
 *
 * ProseMirror text-content positions and Yjs XmlText code-point offsets
 * line up 1:1 for ASCII/BMP-Unicode (which covers 99% of editorial
 * content). Surrogate-pair emojis (U+10000+) are the edge case: PM
 * counts code units, server counts code points. v1 ignores this and
 * may shift tint boundaries by 1-2 chars around supplementary-plane
 * codepoints. Followup: pass through grapheme-cluster boundaries from
 * the server (Task #166 v2).
 *
 * --- Render strategy ---
 *
 * - 0 or 1 run → no tint (paragraph is single-author, the border-bar
 *   from AuthorHighlight already communicates the author).
 * - 2+ runs → render every run as a `Decoration.inline` with a
 *   `background-color: hsla(...)` in the run's author_color at low
 *   alpha (12% saturation). Tooltip via `title` attr.
 * - Pre-V2.3 paragraphs (no authorship_runs attr) → no-op, falls
 *   through to AuthorHighlight's border bar without conflict.
 */

import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

const WORD_LEVEL_AUTHORSHIP_PLUGIN_KEY = new PluginKey('wordLevelAuthorship')

interface AuthorshipRun {
  start: number
  end: number
  author_id: string
  author_name: string | null
  author_color: string | null
  commit_id: string | null
  source: string | null
}

/**
 * Parse the JSON-string `authorship_runs` attr into a validated run list.
 * Returns null on malformed input, empty/missing attr, or single-run
 * coverage (no point in rendering a uniform tint).
 */
function parseRuns(raw: unknown): AuthorshipRun[] | null {
  if (typeof raw !== 'string' || raw.length === 0) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!Array.isArray(parsed)) return null
  if (parsed.length < 2) return null // single-author paragraph → border-bar suffices

  const runs: AuthorshipRun[] = []
  for (const item of parsed) {
    if (typeof item !== 'object' || item === null) return null
    const obj = item as Record<string, unknown>
    if (typeof obj.start !== 'number' || typeof obj.end !== 'number') return null
    if (typeof obj.author_id !== 'string') return null
    runs.push({
      start: obj.start,
      end: obj.end,
      author_id: obj.author_id,
      author_name: typeof obj.author_name === 'string' ? obj.author_name : null,
      author_color: typeof obj.author_color === 'string' ? obj.author_color : null,
      commit_id: typeof obj.commit_id === 'string' ? obj.commit_id : null,
      source: typeof obj.source === 'string' ? obj.source : null,
    })
  }
  return runs
}

/**
 * Convert an `hsl(h, s%, l%)` string to `hsla(h, s%, l%, alpha)`.
 * Falls back to a neutral grey-tint when the input isn't an HSL color
 * (e.g. legacy hex colors or null).
 */
function tintColor(authorColor: string | null, alpha: number): string {
  if (!authorColor) return `hsla(220, 10%, 60%, ${alpha})`
  const match = authorColor.match(/^hsl\((\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?)%,\s*(\d+(?:\.\d+)?)%\)/)
  if (!match) {
    // Hex or unknown format → no tint, browser computes the closest
    // background via CSS alpha-compositing.
    return authorColor
  }
  return `hsla(${match[1]}, ${match[2]}%, ${match[3]}%, ${alpha})`
}

export interface WordLevelAuthorshipOptions {
  /**
   * Background-alpha for each run's tint. Subtle by default — visible
   * enough to spot refinement spans without competing with the actual
   * text. Range 0..1.
   */
  alpha: number
  /**
   * When false, the plugin is a no-op (no decorations emitted).
   * Lets the parent component gate word-level rendering behind a
   * user toggle (e.g. read-mode Author-Highlight switch).
   */
  enabled: boolean
}

export const WordLevelAuthorship = Extension.create<WordLevelAuthorshipOptions>({
  name: 'wordLevelAuthorship',

  addOptions() {
    return {
      alpha: 0.12,
      enabled: true,
    }
  },

  addProseMirrorPlugins() {
    const options = this.options
    return [
      new Plugin({
        key: WORD_LEVEL_AUTHORSHIP_PLUGIN_KEY,
        state: {
          init: () => DecorationSet.empty,
          apply: (tr, oldDeco, _oldState, newState) => {
            if (!options.enabled) return DecorationSet.empty
            if (!tr.docChanged) return oldDeco.map(tr.mapping, tr.doc)

            const decos: Decoration[] = []
            newState.doc.descendants((node, pos) => {
              if (node.type.name !== 'paragraph' && node.type.name !== 'heading') {
                return true
              }
              const attrs = node.attrs as { authorship_runs?: unknown }
              const runs = parseRuns(attrs.authorship_runs)
              if (!runs) return true

              const paraTextLength = node.textContent.length

              for (const run of runs) {
                // Clamp run offsets to the live paragraph length. The
                // sidecar may briefly drift if a write hasn't been
                // shadow-stamped yet — silently shrink rather than
                // emit out-of-range decorations.
                const start = Math.max(0, Math.min(run.start, paraTextLength))
                const end = Math.max(start, Math.min(run.end, paraTextLength))
                if (start >= end) continue

                // PM position math: `pos` is the paragraph's start
                // position (the node itself). `pos + 1` is the first
                // character INSIDE the paragraph. Code-point offsets
                // are added directly — ASCII/BMP cases match 1:1.
                const from = pos + 1 + start
                const to = pos + 1 + end

                const tint = tintColor(run.author_color, options.alpha)
                const title = run.author_name
                  ? `${run.author_name}${run.source ? ` · ${run.source}` : ''}`
                  : run.author_id

                decos.push(
                  Decoration.inline(from, to, {
                    style: `background-color: ${tint}; border-radius: 2px;`,
                    'data-authorship-author-id': run.author_id,
                    'data-authorship-source': run.source || '',
                    title,
                  })
                )
              }
              return true
            })

            return DecorationSet.create(newState.doc, decos)
          },
        },
        props: {
          decorations(state) {
            return WORD_LEVEL_AUTHORSHIP_PLUGIN_KEY.getState(state)
          },
        },
      }),
    ]
  },
})
